import { getConnectionProviderTypes } from "./connectionsApi";
import { ProviderType } from "../../components/common/ObjectTypes";
import { page_size } from "../apiUtils";
import { v1_technologies } from "../../components/common/gundiV1Technologies";

export const loadProviderTypes = async (
    keycloak_token: string,
    page_offset: number,
    providerTypesSearchInput: string,
    providerTypes: ProviderType[],
    append: boolean = false
): Promise<[ProviderType[], number, Error | null, boolean]> => {
    let total_provider_types = 0;

    try {
        const query = {
            offset: page_offset,
            search: providerTypesSearchInput,
            search_fields: "name,value",
            ordering: "name",
            previous:
                process.env.REACT_APP_BASE_URL_LOCAL +
                "/integrations/urls/?is_provider=true&pager=limit&limit=" +
                page_size +
                "&offset=" +
                (page_offset - page_size),
            next:
                process.env.REACT_APP_BASE_URL_LOCAL +
                "/integrations/urls/?is_provider=true&pager=limit&limit=" +
                page_size +
                "&offset=" +
                (page_offset + page_size),
        };

        // Fetch API provider types
        const temp = await getConnectionProviderTypes(keycloak_token, query);
        let apiResults: ProviderType[] = temp?.results ?? [];
        total_provider_types = temp?.count ?? 0;

        // Case 1: No API results and no search input
        if (apiResults.length === 0 && providerTypesSearchInput.length === 0) {
            return [[], 0, null, false];
        }

        // Check if we've reached the end of data
        const hasMoreData = apiResults.length === page_size;

        // Remove duplicates from API results
        const uniqueApiResults = Array.from(new Set(apiResults.map((obj) => obj.id))).map(
            (id) => apiResults.find((obj) => obj.id === id)!
        );

        // Determine if this is the first page
        const isFirstPage = page_offset === 0;
        const lastApiName = uniqueApiResults.length > 0 ? uniqueApiResults[uniqueApiResults.length - 1]?.name?.toLowerCase() : null;

        // Search filtering for v1 technologies
        const filteredV1Results = v1_technologies.filter((provider) => {
            const providerName = provider.name?.toLowerCase() ?? "";
            const providerValue = provider.value?.toLowerCase() ?? "";
            const searchQuery = providerTypesSearchInput.toLowerCase();
            const nameMatches = providerName.includes(searchQuery);
            const valueMatches = providerValue.includes(searchQuery);

            const isWithinAlphabeticalRange = isFirstPage && (!lastApiName || providerName <= lastApiName);

            return (nameMatches || valueMatches) && isWithinAlphabeticalRange;
        });

        // If no API results and search input exists, return only filtered v1 results
        if (uniqueApiResults.length === 0 && providerTypesSearchInput.length > 0) {
            return [filteredV1Results.sort((a, b) => a.name.localeCompare(b.name)), filteredV1Results.length, null, false];
        }

        // Combine API and v1 results (only for first page), then sort alphabetically
        const combinedResults = [...uniqueApiResults, ...filteredV1Results].sort((a, b) =>
            a.name.localeCompare(b.name)
        );

        // If appending, make sure we don't duplicate entries
        if (append) {
            // Create a set of existing IDs for efficient lookup
            const existingIds = new Set(providerTypes.map(item => item.id));

            // Only add items that don't already exist
            const uniqueNewItems = combinedResults.filter(item => !existingIds.has(item.id));

            // Append only unique items
            return [[...providerTypes, ...uniqueNewItems], total_provider_types + filteredV1Results.length, null, hasMoreData];
        } else {
            return [combinedResults, total_provider_types + filteredV1Results.length, null, hasMoreData];
        }
    } catch (err: any) {
        console.error(err);
        return [[], 0, err, false];
    }
};
